import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Paper, Box, Typography, Button, TextField, MenuItem,
  Table, TableBody, TableCell, TableHead, TableRow, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  Chip, Switch, FormControlLabel, Tooltip, InputAdornment,
} from '@mui/material';
import {
  Add, Edit, Delete, Search, Campaign, Settings as SystemIcon,
  NotificationsNone,
} from '@mui/icons-material';
import TopBar from '../../components/TopBar';
import { notificationService, userService } from '../../services/api';

const EMPTY_FORM = {
  type: 'User',
  title: '',
  description: '',
  startDate: '',
  endDate: '',
  isActive: true,
  targetUserId: '',
};

const TYPE_CHIP = {
  User:   { label: 'User',   color: 'secondary', icon: <Campaign   sx={{ fontSize: 14 }} /> },
  System: { label: 'System', color: 'primary',   icon: <SystemIcon sx={{ fontSize: 14 }} /> },
};

export default function NotificationManagement() {
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [users, setUsers]                 = useState([]);
  const [search, setSearch]               = useState('');
  const [filterType, setFilterType]       = useState('All');
  const [filterStatus, setFilterStatus]   = useState('All');

  const [dialogOpen, setDialogOpen]       = useState(false);
  const [editing, setEditing]             = useState(null);   // null = create, obj = edit
  const [form, setForm]                   = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget]   = useState(null);

  const [success, setSuccess]             = useState('');
  const [error, setError]                 = useState('');
  const [saving, setSaving]               = useState(false);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [nRes, uRes] = await Promise.all([
        notificationService.getAll(),
        userService.getAllUsers(),
      ]);
      setNotifications(nRes.data);
      setUsers(uRes.data.filter(u => u.role !== 'SuperAdmin'));
    } catch {
      setError('Failed to load data');
    }
  };

  const flash = (msg, isError = false) => {
    if (isError) { setError(msg); setTimeout(() => setError(''), 4000); }
    else         { setSuccess(msg); setTimeout(() => setSuccess(''), 3000); }
  };

  // ── Dialog helpers ──────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setDialogOpen(true);
  };

  const openEdit = (n) => {
    setEditing(n);
    setForm({
      type:        n.type,
      title:       n.title,
      description: n.description || '',
      startDate:   n.startDate ? n.startDate.split('T')[0] : '',
      endDate:     n.endDate   ? n.endDate.split('T')[0]   : '',
      isActive:    n.isActive,
      targetUserId: n.targetUserId ?? '',
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { flash('Title is required', true); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        startDate:    form.startDate || null,
        endDate:      form.endDate   || null,
        targetUserId: form.targetUserId !== '' ? parseInt(form.targetUserId) : null,
      };
      if (editing) {
        await notificationService.update(editing.id, payload);
        flash('Notification updated');
      } else {
        await notificationService.create(payload);
        flash('Notification created');
      }
      setDialogOpen(false);
      loadAll();
    } catch {
      flash('Failed to save notification', true);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await notificationService.delete(deleteTarget.id);
      flash('Notification deleted');
      setDeleteTarget(null);
      loadAll();
    } catch {
      flash('Failed to delete notification', true);
    }
  };

  // ── Filtering ───────────────────────────────────────────────────────────────
  const today = new Date().toISOString().split('T')[0];
  const filtered = notifications.filter(n => {
    const matchSearch = n.title.toLowerCase().includes(search.toLowerCase()) ||
      (n.description || '').toLowerCase().includes(search.toLowerCase());
    const matchType   = filterType   === 'All' || n.type === filterType;
    const matchStatus = filterStatus === 'All'
      || (filterStatus === 'Active'   && n.isActive)
      || (filterStatus === 'Inactive' && !n.isActive)
      || (filterStatus === 'Expired'  && n.endDate && n.endDate.split('T')[0] < today);
    return matchSearch && matchType && matchStatus;
  });

  const targetLabel = (n) => {
    if (!n.targetUserId) return 'All Users';
    const u = users.find(u => u.id === n.targetUserId);
    return u ? `${u.firstName} ${u.lastName}` : `User #${n.targetUserId}`;
  };

  const statusChip = (n) => {
    if (!n.isActive) return <Chip label="Inactive" size="small" color="default" />;
    if (n.endDate && n.endDate.split('T')[0] < today)
      return <Chip label="Expired" size="small" color="warning" />;
    if (n.startDate && n.startDate.split('T')[0] > today)
      return <Chip label="Scheduled" size="small" color="info" />;
    return <Chip label="Active" size="small" color="success" />;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Notification Management" onBack={() => navigate('/dashboard')} />

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error   && <Alert severity="error"   sx={{ mb: 2 }}>{error}</Alert>}

        <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
          {/* Toolbar */}
          <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', borderBottom: '1px solid #e0e0e0' }}>
            <NotificationsNone sx={{ color: '#1976d2', fontSize: 28 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
              Notifications
              <Chip label={notifications.length} size="small" sx={{ ml: 1, fontWeight: 600 }} />
            </Typography>

            <TextField
              size="small"
              placeholder="Search..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ width: 220 }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 18 }} /></InputAdornment> }}
            />

            <TextField select size="small" label="Type" value={filterType} onChange={e => setFilterType(e.target.value)} sx={{ width: 130 }}>
              {['All', 'User', 'System'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>

            <TextField select size="small" label="Status" value={filterStatus} onChange={e => setFilterStatus(e.target.value)} sx={{ width: 130 }}>
              {['All', 'Active', 'Inactive', 'Expired', 'Scheduled'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>

            <Button variant="contained" startIcon={<Add />} onClick={openCreate}>
              Add Notification
            </Button>
          </Box>

          {/* Table */}
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Title / Description</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 110 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 110 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 200 }}>Date Range</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 150 }}>Target</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 130 }}>Created By</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: 80 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      No notifications found
                    </TableCell>
                  </TableRow>
                ) : filtered.map(n => (
                  <TableRow key={n.id} hover>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>{n.title}</Typography>
                      {n.description && (
                        <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.2 }}
                          noWrap title={n.description}>
                          {n.description.length > 80 ? n.description.slice(0, 80) + '…' : n.description}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={TYPE_CHIP[n.type]?.label || n.type}
                        icon={TYPE_CHIP[n.type]?.icon}
                        color={TYPE_CHIP[n.type]?.color || 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{statusChip(n)}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>
                      {n.startDate ? new Date(n.startDate).toLocaleDateString('en-IN') : '—'}
                      {' → '}
                      {n.endDate   ? new Date(n.endDate).toLocaleDateString('en-IN')   : 'No expiry'}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{targetLabel(n)}</TableCell>
                    <TableCell sx={{ fontSize: '0.78rem' }}>{n.createdByName || '—'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openEdit(n)}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteTarget(n)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Paper>
      </Container>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editing ? 'Edit Notification' : 'New Notification'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>

            {/* Type */}
            <TextField
              select fullWidth label="Notification Type" value={form.type}
              onChange={e => setForm({ ...form, type: e.target.value })}
            >
              <MenuItem value="User">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Campaign fontSize="small" color="secondary" /> User Notification
                </Box>
              </MenuItem>
              <MenuItem value="System">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SystemIcon fontSize="small" color="primary" /> System Notification
                </Box>
              </MenuItem>
            </TextField>

            {/* Title */}
            <TextField
              fullWidth required label="Title" value={form.title}
              onChange={e => setForm({ ...form, title: e.target.value })}
              inputProps={{ maxLength: 200 }}
              helperText={`${form.title.length}/200`}
            />

            {/* Description */}
            <TextField
              fullWidth multiline rows={3} label="Description"
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              inputProps={{ maxLength: 2000 }}
              helperText="Shown below the title in the notification panel"
            />

            {/* Date range */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth type="date" label="Start Date"
                value={form.startDate}
                onChange={e => setForm({ ...form, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Visible from (blank = immediately)"
              />
              <TextField
                fullWidth type="date" label="End Date"
                value={form.endDate}
                onChange={e => setForm({ ...form, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Visible until (blank = no expiry)"
                inputProps={{ min: form.startDate || undefined }}
              />
            </Box>

            {/* Target user */}
            <TextField
              select fullWidth label="Target Audience"
              value={form.targetUserId}
              onChange={e => setForm({ ...form, targetUserId: e.target.value })}
              helperText="Send to all users or a specific staff member"
            >
              <MenuItem value="">All Users</MenuItem>
              {users.map(u => (
                <MenuItem key={u.id} value={u.id}>
                  {u.firstName} {u.lastName} — {u.role}
                </MenuItem>
              ))}
            </TextField>

            {/* Active toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={e => setForm({ ...form, isActive: e.target.checked })}
                  color="success"
                />
              }
              label={form.isActive ? 'Active (visible to users)' : 'Inactive (hidden from users)'}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Notification'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Notification?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete <strong>"{deleteTarget?.title}"</strong>?
            This will also remove all dismiss records for this notification.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 3, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
